import { AlertCircle, ScrollText } from "lucide-react";

import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import { AuditLogDetailPanel } from "./AuditLogDetailPanel";
import { AuditLogTable } from "./AuditLogTable";
import { AuditLogToolbar } from "./AuditLogToolbar";
import { useAuditLog } from "./use-audit-log";

export function AuditLogPage(): React.ReactElement {
  const {
    entries,
    isLoading,
    error,
    filters,
    setFilters,
    hasMore,
    hasPrevious,
    pageNumber,
    loadNext,
    loadPrevious,
    reload,
    selectedEntry,
    selectEntry,
  } = useAuditLog();

  return (
    <div data-testid="audit-log-page" className="space-y-6">
      <AuditLogToolbar filters={filters} onFiltersChange={setFilters} isLoading={isLoading} />

      {error && (
        <div
          data-testid="audit-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={reload}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && (
        <div data-testid="audit-loading" className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && entries.length === 0 && !error && (
        <Card data-testid="empty-audit-card" className="mx-auto max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <ScrollText className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>No Audit Events</CardTitle>
            <CardDescription>
              No audit events match the current filters. Try adjusting the action filter.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && entries.length > 0 && (
        <div className="flex gap-6">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center justify-between">
              <span data-testid="audit-result-count" className="text-sm text-muted-foreground">
                Showing {entries.length} {entries.length === 1 ? "result" : "results"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="audit-prev-page"
                  variant="outline"
                  size="sm"
                  disabled={!hasPrevious}
                  onClick={loadPrevious}
                >
                  Previous
                </Button>
                <span data-testid="audit-page-info" className="text-sm text-muted-foreground">
                  Page {pageNumber}
                </span>
                <Button
                  data-testid="audit-next-page"
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={loadNext}
                >
                  Next
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="p-0">
                <AuditLogTable
                  entries={entries}
                  selectedEntry={selectedEntry}
                  onSelect={selectEntry}
                />
              </CardContent>
            </Card>
          </div>

          {selectedEntry && (
            <div className="w-96 shrink-0">
              <AuditLogDetailPanel entry={selectedEntry} onClose={() => selectEntry(null)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
